// ===========================================
// SCROLL ANIMATIONS (GSAP + ScrollTrigger) and smooth scrolling (Lenis, desktop only).
//
// Markup:
//   data-gsap-animate="fade-up"      rises and fades in ("slide-up" is the same)
//   data-gsap-animate="fade-in"      fades in
//   data-gsap-animate="slide-left"   slides in from the left
//   data-gsap-animate="slide-right"  slides in from the right
//   data-gsap-animate="scale"        grows slightly and fades in
//   data-gsap-animate="stagger"      each child rises and fades in as it scrolls into view
//   data-gsap-animate="image"        fades in, the image inside settles from a slight zoom
//   data-gsap-delay="150"            optional delay in ms
//
// Each element plays once. Hidden-before-reveal CSS: assets/scss/partials/_animations.scss
// (only while <html> has .has-gsap, set in the <head> by sme_animations_flag()).
// ===========================================

import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Lenis from "lenis";

gsap.registerPlugin(ScrollTrigger);

const START = "top 85%";
const EASE = "power3.out";

const REVEALS = {
  "fade-up": { from: { y: 40 }, duration: 1.2 },
  "slide-up": { from: { y: 40 }, duration: 1.2 },
  "fade-in": { from: {}, duration: 1.4 },
  "slide-left": { from: { x: -60 }, duration: 1.3 },
  "slide-right": { from: { x: 60 }, duration: 1.3 },
  scale: { from: { scale: 0.96 }, duration: 1.3 },
};

function done(targets) {
  gsap.set(targets, { clearProps: "opacity,transform" });
  [].concat(targets).forEach((el) => el.classList.add("gsap-animated"));
}

function delayOf(el) {
  return (parseInt(el.dataset.gsapDelay, 10) || 0) / 1000;
}

function reveal(el, { from, duration }) {
  gsap.fromTo(
    el,
    { opacity: 0, ...from },
    {
      opacity: 1,
      x: 0,
      y: 0,
      scale: 1,
      duration,
      delay: delayOf(el),
      ease: EASE,
      scrollTrigger: { trigger: el, start: START, once: true },
      onComplete: () => done(el),
    }
  );
}

function image(el) {
  const img = el.querySelector(".image") || el.querySelector("img");

  const tl = gsap.timeline({
    delay: delayOf(el),
    scrollTrigger: { trigger: el, start: START, once: true },
    onComplete: () => done(img ? [el, img] : el),
  });

  tl.fromTo(el, { opacity: 0 }, { opacity: 1, duration: 1.2, ease: "power2.out" });

  if (img) {
    tl.fromTo(img, { scale: 1.06 }, { scale: 1, duration: 1.8, ease: EASE }, 0);
  }
}

function stagger(el) {
  const items = Array.from(el.children);
  const delay = delayOf(el);

  el.classList.add("gsap-animated");

  if (!items.length) {
    return;
  }

  ScrollTrigger.batch(items, {
    start: START,
    once: true,
    onEnter: (batch) =>
      gsap.fromTo(
        batch,
        { opacity: 0, y: 32 },
        {
          opacity: 1,
          y: 0,
          duration: 1.1,
          delay,
          stagger: 0.12,
          ease: EASE,
          onComplete: () => done(batch),
        }
      ),
  });
}

// Desktop only, like the other sites: touch devices keep native scrolling.
function smoothScroll() {
  if (window.innerWidth <= 768 || !window.matchMedia("(pointer: fine)").matches) {
    return;
  }

  const lenis = new Lenis({
    duration: 1.4,
    easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    allowNestedScroll: true,
  });

  lenis.on("scroll", ScrollTrigger.update);
  gsap.ticker.add((time) => lenis.raf(time * 1000));
  gsap.ticker.lagSmoothing(0);

  // Mobile menu (assets/js/mobile-menu.js) locks the page while open.
  new MutationObserver(() => {
    document.body.classList.contains("menu-open") ? lenis.stop() : lenis.start();
  }).observe(document.body, { attributes: true, attributeFilter: ["class"] });

  window.smeLenis = lenis;
}

function init() {
  const root = document.documentElement;

  if (!root.classList.contains("has-gsap")) {
    return;
  }

  smoothScroll();

  document.querySelectorAll("[data-gsap-animate]").forEach((el) => {
    const type = el.dataset.gsapAnimate;

    if (REVEALS[type]) {
      reveal(el, REVEALS[type]);
    } else if ("image" === type) {
      image(el);
    } else if ("stagger" === type) {
      stagger(el);
    } else {
      el.classList.add("gsap-animated");
    }
  });

  window.smeGsapReady = true;

  // Fonts and lazy images change the layout after load.
  window.addEventListener("load", () => ScrollTrigger.refresh());
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
